import {useState, useRef, useEffect, useCallback, useMemo, CSSProperties} from "react";
import {useFormContext} from "react-hook-form";
import {Badge} from "@/components/ui/badge";
import {X, Plus} from "lucide-react";
import {cn} from "@/lib/utils";
import {useClinicalTerms} from "@/lib/clinicalTerms";
import TermSuggestion from "./TermSuggestion";
import SectionPropertiesProps from "@/components/shared/SectionProperties.tsx";
import { TemplateMenu, TemplateOption, useApplyTemplate, useTemplateMatches, type RxTemplate } from "@/components/Template/RxTemplates";

const getInvestigationName = (item: any): string =>
    typeof item === "string" ? item : typeof item?.investigation === "string" ? item.investigation : "";

export default function InvestigationsGrid({specialty = "General Medicine", patientAgeMonths, patient}: SectionPropertiesProps) {
    const {setValue, watch, formState: {errors}} = useFormContext();
    const [searchInput, setSearchInput] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);

    // Re-position the dropdown when the on-screen keyboard (tablets) resizes the visible viewport
    const [, setViewportTick] = useState(0);
    useEffect(() => {
        const vv = window.visualViewport;
        if (!showSuggestions || !vv) return;
        const onChange = () => setViewportTick((t) => t + 1);
        vv.addEventListener("resize", onChange);
        vv.addEventListener("scroll", onChange);
        return () => {
            vv.removeEventListener("resize", onChange);
            vv.removeEventListener("scroll", onChange);
        };
    }, [showSuggestions]);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const getDropdownStyle = (): CSSProperties => {
        if (!inputRef.current) return {};
        const rect = inputRef.current.getBoundingClientRect();
        const dropdownHeight = Math.min(8 * 36 + 8, 224);
        // Visible bottom edge — excludes the on-screen keyboard on tablets
        const visibleBottom = window.visualViewport
            ? window.visualViewport.offsetTop + window.visualViewport.height
            : window.innerHeight;
        const wouldOverflowBottom = rect.bottom + dropdownHeight > visibleBottom - 20;
        const wouldOverflowRight = rect.left + 280 > window.innerWidth - 20;
        return {
            position: "fixed",
            minWidth: 280,
            zIndex:   9999,
            ...(wouldOverflowRight ? { right: window.innerWidth - rect.right } : { left: rect.left }),
            ...(wouldOverflowBottom
                ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
                : { top: rect.bottom + 2 }),
        };
    };

    // 💡 Native Array Watcher logic (Matches your dedicated relational table)
    const rawSelected: any[] = watch("investigations") || [];

    const selectedTests = useMemo(() => {
        return rawSelected.map(getInvestigationName).filter(Boolean);
    }, [rawSelected]);

    // Empty box: this doctor's most-ordered tests, then the usual ones for the specialty / patient; typing
    // searches the whole list, lab short forms included ("cbp" → CBC, "rft" → KFT)
    const testTerms = useClinicalTerms("test");
    const matches = useMemo(
        () => testTerms.suggest(searchInput, { sex: patient?.gender, ageMonths: patientAgeMonths }, specialty, new Set(selectedTests.map((t) => t.toLowerCase()))),
        [testTerms, searchInput, patient?.gender, patientAgeMonths, specialty, selectedTests]
    );
    const filteredSuggestions = matches.map((m) => m.term.name);

    useEffect(() => {
        setFocusedIndex(showSuggestions && filteredSuggestions.length > 0 ? 0 : -1);
    }, [showSuggestions, filteredSuggestions.length]);

    const addInvestigation = useCallback((test: string) => {
        const trimmed = test.trim();
        if (trimmed && !selectedTests.includes(trimmed)) {
            setValue("investigations", [...rawSelected, trimmed], {shouldDirty: true});
        }
        setSearchInput("");
        setTimeout(() => {
            inputRef.current?.focus();
            setShowSuggestions(true);
        }, 0);
    }, [selectedTests, rawSelected, setValue]);

    const removeInvestigation = useCallback((testName: string) => {
        const updated = rawSelected.filter((item) => getInvestigationName(item) !== testName);
        setValue("investigations", updated, {shouldDirty: true});
    }, [rawSelected, setValue]);

    // Saved templates whose name is being typed — listed after the suggestions, reached with the arrows
    const templates = useTemplateMatches("investigation", searchInput);
    const applyTemplate = useApplyTemplate();
    const pickTemplate = (template: RxTemplate) => {
        applyTemplate?.(template);
        setSearchInput("");
        setShowSuggestions(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const max = filteredSuggestions.length + templates.length - 1;
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setFocusedIndex((p) => (p < max ? p + 1 : 0));
                break;
            case "ArrowUp":
                e.preventDefault();
                setFocusedIndex((p) => (p > 0 ? p - 1 : max));
                break;
            case "Enter":
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < filteredSuggestions.length) {
                    addInvestigation(filteredSuggestions[focusedIndex]);
                } else if (focusedIndex >= filteredSuggestions.length && focusedIndex <= max) {
                    pickTemplate(templates[focusedIndex - filteredSuggestions.length]);
                } else if (searchInput.trim()) {
                    addInvestigation(searchInput.trim());
                }
                break;
            case "Escape":
                setShowSuggestions(false);
                break;
            case "Backspace":
                if (searchInput === "" && selectedTests.length > 0) {
                    e.preventDefault();
                    removeInvestigation(selectedTests[selectedTests.length - 1]);
                }
                break;
            case "Tab":
                setShowSuggestions(false);
                break;
        }
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setShowSuggestions(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="space-y-2" ref={containerRef}>
            <div
                className={cn(
                    "min-h-9 touch:min-h-10 px-1.5 py-[3px] border border-field rounded-md cursor-text transition-all bg-card flex items-center",
                    "hover:border-ring/60",
                    showSuggestions && "ring-2 ring-primary/20 border-primary",
                    errors.investigations && "border-destructive/30 bg-destructive/5"
                )}
                onClick={() => {
                    inputRef.current?.focus();
                    setShowSuggestions(true);
                }}
            >
                <div className="flex flex-wrap items-center gap-1.5 w-full">
                    {selectedTests.map((name, idx) => (
                        <Badge key={`${name}-${idx}`} variant="secondary"
                               className="h-[26px] touch:h-8 px-2 rounded text-[13px] font-normal bg-muted text-foreground border-0 hover:bg-muted">
                            <span className="font-medium">{name}</span>
                            <button type="button" onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                removeInvestigation(name);
                            }} className="touch-target ml-1.5 p-1 -m-1 hover:text-destructive">
                                <X className="w-3 h-3"/>
                            </button>
                        </Badge>
                    ))}
                    <input
                        ref={inputRef} type="text" value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                            setShowSuggestions(true);
                        }}
                        onFocus={() => setShowSuggestions(true)} onKeyDown={handleKeyDown}
                        placeholder={selectedTests.length === 0 ? "Add test…" : "Add another…"}
                        className="flex-1 min-w-[160px] h-7 touch:h-9 px-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground/60 placeholder:font-normal"
                    />
                    <TemplateMenu type="investigation" />
                </div>
            </div>

            {showSuggestions && (filteredSuggestions.length > 0 || searchInput.trim()) && (
                <div
                    className="bg-popover border border-border rounded-lg shadow-lg max-h-56 overflow-y-auto"
                    style={getDropdownStyle()}
                >
                    {matches.map((m, index) => (
                        <TermSuggestion
                            key={m.term.id}
                            match={m}
                            active={index === focusedIndex}
                            onPick={() => addInvestigation(m.term.name)}
                            onForget={() => testTerms.forget(m.term.id)}
                        />
                    ))}
                    {templates.map((t, i) => (
                        <TemplateOption key={t.id} template={t} active={filteredSuggestions.length + i === focusedIndex} onPick={() => pickTemplate(t)} />
                    ))}
                            {searchInput.trim() && !filteredSuggestions.some(t => t.toLowerCase() === searchInput.toLowerCase().trim()) && !testTerms.find(searchInput) && (
                                <button type="button" onMouseDown={(e) => {
                                    e.preventDefault();
                                    addInvestigation(searchInput.trim());
                                }}
                                        className="w-full text-left px-3 py-2 text-sm border-t border-border flex items-center justify-between text-muted-foreground hover:bg-accent">
                                    <span>Add custom: "{searchInput.trim()}"</span>
                                    <Plus className="w-3 h-3 text-[hsl(var(--success))]"/>
                                </button>
                            )}
                </div>
            )}
        </div>
    );
}